// Horse attributes + jump charge (M19-adjacent). PURE (ENG-1).
//
// Every horse in Minecraft is a slightly different animal: its speed, jump height
// and health are rolled at spawn from three independent draws, which is why finding
// a good one is worth doing. And a ridden horse doesn't just hop — you hold jump to
// wind up a charge and release it to leap, with the height scaling on both the
// charge and the horse's own jump attribute.
import { HORSE } from '../config';

/** A horse's rolled attributes, as multipliers on its def's base values. */
export interface HorseAttributes {
  speedMul: number;
  jumpMul: number;
  healthMul: number;
}

/** Java rolls each attribute from the mean of three uniform draws, which clusters
 *  values toward the middle — most horses are average, a few are exceptional. */
/** The mean of three [0,1) draws — a bell-ish shape on [0,1). Exported because heredity
 *  rolls polygenic genes with the SAME shape (ARCH §28.4), so a heredity horse and a
 *  classic one are drawn from one distribution. */
export function tripleAverage(a: number, b: number, c: number): number {
  return (a + b + c) / 3;
}

/** Roll a horse's attributes from three [0,1) draws per attribute. */
export function rollHorseAttributes(next: () => number): HorseAttributes {
  const span = (lo: number, hi: number): number => lo + tripleAverage(next(), next(), next()) * (hi - lo);
  return {
    speedMul: span(HORSE.SPEED_MIN, HORSE.SPEED_MAX),
    jumpMul: span(HORSE.JUMP_MIN, HORSE.JUMP_MAX),
    healthMul: span(HORSE.HEALTH_MIN, HORSE.HEALTH_MAX),
  };
}

/** Advance a held jump charge (0..1); releasing is the host's business. */
export function chargeJump(current: number, holding: boolean): number {
  if (!holding) return 0;
  return Math.min(1, current + 1 / HORSE.CHARGE_TICKS);
}

/** The upward impulse a released charge gives, scaled by the horse's jump attribute.
 *  A charge below the minimum is a refusal — you get nothing, not a limp hop. */
export function jumpImpulse(charge: number, jumpMul: number): number {
  if (charge < HORSE.MIN_CHARGE) return 0;
  return HORSE.BASE_JUMP * charge * jumpMul;
}

/** Does a mount of this kind wind up a charged jump (horses and their kin do)? */
export function chargesJump(kind: string): boolean {
  return kind === 'horse' || kind === 'donkey' || kind === 'mule';
}
